<script setup lang="ts">
import { mdiClose } from "@mdi/js";
import FilterExpandable from "~/components/layout/Sport/SportsBook/EventFilters/Modal/FilterExpandable.vue";
import NavIcon from "~/components/layout/Nav/NavIcon.vue";
import Checkbox from "~/components/ui/Form/Checkbox.vue";
import { IRegion } from "~/types/sports/region";
import SubTabs from "~/components/ui/SubTabs.vue";
import t from "~/utils/translate";
import slugify from "~/utils/sport/slugify";
import { ISelection } from "~/types/swipe-bet/filter";

interface Props {
	filter: IRegion[];
	selected: ISelection[];
}

const tab = ref("all");
defineProps<Props>();
const emits = defineEmits(["update-regions", "remove-region"]);
</script>

<template>
	<div class="relative">
		<SubTabs
			class="sticky top-0 left-0 z-10 bg-light-200 dark:bg-dark-800"
			:initial-tab="tab"
			@on-update="(val) => (tab = val)"
			:tabs="[
				{ name: t('all-leagues'), value: 'all' },
				{ name: t('selection'), value: 'selection', count: selected?.length || 0 },
			]"
		/>
		<div v-if="tab === 'all'">
			<FilterExpandable v-for="region in filter" :key="region.name" :initial-state="false" :expandable="true">
				<template #header>
					<div class="flex gap-1">
						<NavIcon
							:width="24"
							:height="24"
							:src="`/icons/regions-leagues-flags/${slugify(region.name)}-circular.svg`"
							:alt="region.name"
							fallback="/icons/regions-leagues-flags/default-circular.svg"
							class="flex-shrink-0"
						/>
						<strong>{{ t(region.name) }}</strong>
					</div>
				</template>
				<div class="grid grid-cols-2">
					<div
						class="flex items-center p-2 border-b border-r border-light-400 dark:border-dark-500"
						v-for="league in region.leagues"
						:key="`${league.leagueId}-${region.regionId}`"
					>
						<Checkbox
							:model-value="selected && selected.find((x) => x.leagueId === league.name && x.regionId === region.name) ? true : false"
							@update:model-value="
								(val) => {
									if (val === true) emits('update-regions', { regionId: region.name, leagueId: league.name });
									else emits('remove-region', { regionId: region.name, leagueId: league.name });
								}
							"
							:label="t(league.name)"
						/>
					</div>
					<div v-if="region.leagues.length % 2" class="w-full border-b border-r border-light-400 dark:border-dark-500" />
				</div>
			</FilterExpandable>
		</div>

		<div v-else-if="tab === 'selection'">
			<div v-if="!selected || selected?.length <= 0" class="flex items-center justify-center p-2 text-xs">
				{{ t("no-selection-available") }}
			</div>
			<div
				v-else
				v-for="selection in selected"
				:key="`selection-${selection.regionId}-${selection.leagueId}`"
				class="relative w-full p-2 pr-10 text-xs border-b border-b-light-400 bg-light-50 dark:bg-dark-600 dark:border-b-dark-500"
			>
				<strong class="capitalize">{{ t(`sports.${selection.sport}`) }} </strong>, {{ t(selection.leagueId) }},
				{{ t(selection.regionId) }}
				<UiIcon
					@click="emits('remove-region', { regionId: selection.regionId, leagueId: selection.leagueId })"
					:path="mdiClose"
					class="absolute w-5 h-5 -translate-y-1/2 cursor-pointer right-2 top-1/2 dark:fill-light-50"
				/>
			</div>
		</div>
	</div>
</template>
